import * as countriesModule from "intl-tel-input/data";
import { Language, allLanguageCodes } from "@interfaces/translation-interface";

const regionCurrency: Record<string, string> = {
  GB: "GBP", US: "USD", CA: "CAD", AU: "AUD", NZ: "NZD",
  IN: "INR", BR: "BRL", CH: "CHF", ZA: "ZAR", MX: "MXN",
  AR: "ARS", CO: "COP", CL: "CLP", PE: "PEN", SG: "SGD",
  HK: "HKD", JP: "JPY", AE: "AED",
  AT: "EUR", BE: "EUR", CY: "EUR", DE: "EUR", EE: "EUR",
  ES: "EUR", FI: "EUR", FR: "EUR", GR: "EUR", HR: "EUR",
  IE: "EUR", IT: "EUR", LT: "EUR", LU: "EUR", LV: "EUR",
  MT: "EUR", NL: "EUR", PT: "EUR", SI: "EUR", SK: "EUR",
};

const allCountries = Array.isArray(countriesModule)
  ? countriesModule
  : (countriesModule as any).default;
const dialByRegion = new Map<string, string>(allCountries.map(country =>
  [country.iso2.toUpperCase(), `+${country.dialCode}`]));

export interface RegistrationDefaults {
  language: Language;
  currency: string;
  dialCode: string;
  browserRegion: string;
  hasUrlDial: boolean;
  hasUrlCurrency: boolean;
  /** True only when a supported language was resolved from URL params. */
  hasUrlLanguage: boolean;
}

/** Cross-domain / URL language aliases in precedence order (first valid wins). */
export const URL_LANGUAGE_PARAM_NAMES = [
  "lang_code",
  "language_code",
  "lang",
  "language",
] as const;

/**
 * Returns the first allowlisted language from URL params, or null if none valid.
 * Invalid higher-priority params do not block lower-priority valid ones.
 */
export function resolveUrlLanguage(params: Record<string, unknown>): Language | null {
  const keys = Object.keys(params || {});
  for (const name of URL_LANGUAGE_PARAM_NAMES) {
    const key = keys.find((candidate) => candidate.toLowerCase() === name);
    if (!key || typeof params[key] !== "string") {
      continue;
    }
    const candidate = String(params[key]).trim().toLowerCase();
    if (allLanguageCodes.includes(candidate as Language)) {
      return candidate as Language;
    }
  }
  return null;
}

export function registrationDefaults(params: Record<string, unknown>, browserLanguages: readonly string[]): RegistrationDefaults {
  const urlValue = (...names: string[]): string => {
    const key = Object.keys(params || {}).find(candidate => names.includes(candidate.toLowerCase()));
    return key && typeof params[key] === "string" ? String(params[key]).trim() : "";
  };

  let browserLanguage = "";
  let browserRegion = "";
  for (const tag of browserLanguages || []) {
    try {
      const locale = new Intl.Locale(tag);
      const language = locale.language.toLowerCase();
      if (allLanguageCodes.includes(language as Language)) {
        browserLanguage = language;
        browserRegion = locale.region?.toUpperCase() || "";
        break;
      }
    } catch { /* Ignore malformed browser locales. */ }
  }

  // Cross-domain handoffs use lang_code / language_code (login, GiveawayKing, etc.)
  const urlLanguage = resolveUrlLanguage(params);
  const hasUrlLanguage = !!urlLanguage;
  const language = urlLanguage || (browserLanguage as Language || Language.English);

  const requestedDial = urlValue("dial_code", "calling_code", "country_code");
  const requestedRegion = (urlValue("country", "country_iso", "country_iso2") ||
    (/^[A-Za-z]{2}$/.test(requestedDial) ? requestedDial : "")).toUpperCase();
  const numericDial = /^\+?[1-9]\d{0,3}$/.test(requestedDial)
    ? `+${requestedDial.replace(/^\+/, "")}` : "";
  const regionDial = dialByRegion.get(requestedRegion) || "";
  const hasUrlDial = !!(numericDial || regionDial);
  // English keeps LIVE product defaults (+44/GBP). Non-English may use browser region.
  // Avoid en-US → +1/USD, which caused the LIVE UT revert.
  const useEnglishDefaults = language === Language.English;
  const dialCode = numericDial || regionDial
    || (useEnglishDefaults ? "+44" : (dialByRegion.get(browserRegion) || "+44"));

  const requestedCurrency = urlValue("currency", "curr").toUpperCase();
  const hasUrlCurrency = /^[A-Z]{3}$/.test(requestedCurrency);
  const currency = hasUrlCurrency
    ? requestedCurrency
    : (useEnglishDefaults ? "GBP" : (regionCurrency[browserRegion] || "GBP"));

  return { language, currency, dialCode, browserRegion, hasUrlDial, hasUrlCurrency, hasUrlLanguage };
}
